import { Button, Form, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import { useContext, useState, useEffect, useRef } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import SupportService from '../../../services/Support';
import { useParams } from 'react-router-dom';
import { findOversizedAttachment, useSupportAttachmentConfig } from '../../../hooks/useSupportAttachmentConfig';

type SupportReplyNewProps = {
  onCreateReply: () => void;
  schoolId?: string;
};

const SupportReplyNew = ({ onCreateReply, schoolId }: SupportReplyNewProps) => {
  const { token }: any = useContext(AuthContext);
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id } = useParams<{ id: string }>();
  const { maxSizeMb, accept } = useSupportAttachmentConfig(schoolId);

  useEffect(() => {
    setIsDisabled(false);
    setDescription('');
    setAttachments([]);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    const trimmedDescription = description.trim();
    if (!trimmedDescription && attachments.length === 0) {
      toast.warn('Enter a message or attach at least one file.');
      return;
    }

    setLoading(true);
    setIsDisabled(false);

    try {
      let attachmentRefs: string[] = [];
      if (attachments.length > 0) {
        const formData = new FormData();
        attachments.forEach((file) => formData.append('attachments', file));
        const uploaded = await SupportService(token).replyAttachment(id, formData);
        attachmentRefs = Array.isArray(uploaded?.data)
          ? uploaded.data
              .map((item: { name?: string }) => item?.name)
              .filter((value: string | undefined): value is string => Boolean(value))
          : [];
      }

      const body = {
        description: trimmedDescription,
        attachmentRefs,
      };
      await SupportService(token).createReply(id, body);
      toast.success('Created successfully!');
      onCreateReply();
      setDescription('');
      setAttachments([]);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Create error!');
      toast.error(message);
    }

    setLoading(false);
  };

  const openFilePicker = () => {
    if (isDisabled || loading) return;
    fileInputRef.current?.click();
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';

    const oversizedFile = findOversizedAttachment(files, maxSizeMb);
    if (oversizedFile) {
      toast.warn(`"${oversizedFile.name}" is larger than the ${maxSizeMb}MB limit per file.`);
      return;
    }

    setAttachments(files);
  };

  const canSubmitReply = description.trim().length > 0 || attachments.length > 0;

  return (
    <Form onSubmit={handleSubmit} className="mt-4">
      <div className="d-flex rounded border bg-white align-items-stretch overflow-hidden shadow-sm">
        <Input
          type="textarea"
          className="border-0 shadow-none mb-0"
          rows={3}
          id="support-reply-input"
          placeholder="Type your message..."
          disabled={isDisabled}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <input
          ref={fileInputRef}
          type="file"
          id="support-reply-attachments"
          className="d-none"
          multiple
          accept={accept}
          onChange={handleFilesChange}
          disabled={isDisabled || loading}
        />
        <Button
          type="button"
          color="primary"
          className="rounded-0 border-left border-0 flex-shrink-0 align-self-stretch d-flex align-items-center justify-content-center px-3 py-0 m-0"
          style={{ minWidth: '3.25rem' }}
          onClick={openFilePicker}
          disabled={isDisabled || loading}
          aria-label="Attach files"
          title="Attach files"
        >
          <i className="fas fa-paperclip text-white" />
        </Button>
        <Button
          color="dark"
          className="rounded-0 px-4 d-flex align-items-center m-0"
          type="submit"
          disabled={isDisabled || loading || !canSubmitReply}
        >
          <i className="fas fa-paper-plane" />
        </Button>
      </div>
      {attachments.length > 0 && (
        <small className="text-muted mt-2 d-block">{attachments.map((file) => file.name).join(', ')}</small>
      )}
    </Form>
  );
};

export default SupportReplyNew;
